/* Shared formatting helpers. Each sensor component used to carry its own copy
   of these, with slightly different output — hence timestamps that looked
   different from card to card. */

export const EM_DASH = "—";

/** Absolute local timestamp, e.g. "12 Aug 2026, 14:05". */
export const formatTimestamp = (ts) => {
  const value = Number(ts);
  if (!Number.isFinite(value)) return EM_DASH;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EM_DASH;
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Time only — used for dense x-axis labels. */
export const formatTime = (ts) => {
  const value = Number(ts);
  if (!Number.isFinite(value)) return EM_DASH;
  return new Date(value).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/** "3m ago" / "in 2h" — Intl-backed so it localises. */
export const formatRelative = (ts) => {
  const value = Number(ts);
  if (!Number.isFinite(value)) return EM_DASH;

  const deltaSeconds = Math.round((value - Date.now()) / 1000);
  const abs = Math.abs(deltaSeconds);
  const units = [
    { limit: 60, divisor: 1, unit: "second" },
    { limit: 3600, divisor: 60, unit: "minute" },
    { limit: 86400, divisor: 3600, unit: "hour" },
    { limit: 604800, divisor: 86400, unit: "day" },
    { limit: 2629800, divisor: 604800, unit: "week" },
    { limit: 31557600, divisor: 2629800, unit: "month" },
  ];
  const match =
    units.find((u) => abs < u.limit) || { divisor: 31557600, unit: "year" };

  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  return formatter.format(Math.round(deltaSeconds / match.divisor), match.unit);
};

/** Fixed-precision number, or an em dash when there is nothing to show. */
export const formatNumber = (value, digits = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return EM_DASH;
  return num.toFixed(digits);
};

/** Summary statistics over a numeric array. */
export const describe = (values = [], digits = 1) => {
  const nums = values.map(Number).filter(Number.isFinite);
  if (!nums.length) {
    return { count: 0, min: EM_DASH, max: EM_DASH, avg: EM_DASH, range: EM_DASH };
  }
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const avg = nums.reduce((sum, n) => sum + n, 0) / nums.length;
  return {
    count: nums.length,
    min: min.toFixed(digits),
    max: max.toFixed(digits),
    avg: avg.toFixed(digits),
    range: `${min.toFixed(digits)}–${max.toFixed(digits)}`,
  };
};

/** Mean of a numeric field over records falling in the current month. */
export const monthlyAverage = (records = [], pick, digits = 1) => {
  const now = new Date();
  const inMonth = records.filter((record) => {
    const date = new Date(Number(record.timestamp));
    return (
      date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()
    );
  });
  const values = inMonth.map(pick).map(Number).filter(Number.isFinite);
  if (!values.length) return EM_DASH;
  return (values.reduce((sum, n) => sum + n, 0) / values.length).toFixed(digits);
};

/** Newest record by timestamp. */
export const latestOf = (records = []) => {
  if (!records.length) return null;
  return records
    .slice()
    .sort((a, b) => Number(b.timestamp) - Number(a.timestamp))[0];
};
